import React, {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import CountUp from 'react-countup';
import { FormattedMessage } from 'react-intl';

import { RouteComponentProps, navigate, useLocation } from '@reach/router';
import { useEtherBalance, useEthers } from '@usedapp/core';

import AmountFiat from '@bitcoin-portal/verse-web-components/dist/AmountFiat';
import Body from '@bitcoin-portal/verse-web-components/dist/Body';
import Button from '@bitcoin-portal/verse-web-components/dist/Button';
import { Card } from '@bitcoin-portal/verse-web-components/dist/Card';
import Coin from '@bitcoin-portal/verse-web-components/dist/Coin';
import CoinPair from '@bitcoin-portal/verse-web-components/dist/CoinPair';
import H2 from '@bitcoin-portal/verse-web-components/dist/H2';
import External from '@bitcoin-portal/verse-web-components/dist/Icons/External';
import Label from '@bitcoin-portal/verse-web-components/dist/Label';
import Link from '@bitcoin-portal/verse-web-components/dist/Link';
import Modal from '@bitcoin-portal/verse-web-components/dist/Modal/Modal';
import Title from '@bitcoin-portal/verse-web-components/dist/Title';
import { colors } from '@bitcoin-portal/verse-web-components/dist/themes/colors';

import { NeedEthModal } from '@components/NeedEthModal';
import ScrollToTop from '@components/ScrollToTop';

import {
  DEFAULT_DECIMALS,
  EMPTY_ACCOUNT,
  PRECISION,
  USDT_WZANO_FARM,
} from '@context/constants';
import { useFarms } from '@context/providers/FarmsProvider';
import { useTrackedState } from '@context/store';

import { formatValue } from '@helpers/formatValue';
import {
  findExternalIcon,
  findProvider,
  lpTokenValue,
  toFixed,
} from '@helpers/index';

import { FarmsView } from '../FarmsWidget';

import {
  ButtonsWrapper,
  ClaimsSummary,
  CoinWrapper,
  DepositButton,
  DetailHeader,
  DetailsWrapper,
  InvestmentSummary,
  PositionWrapper,
  RewardsWrapper,
  StyledChevron,
  WithdrawWrapper,
} from './styled';

interface InvestmentDetailProp extends RouteComponentProps {
  stakedBalance: number;
  readableLPTokenBalance: number;
  makeClaim: () => void;
  view?: FarmsView;
  pendingView?: ReactNode;
  successView?: ReactNode;
  errorView?: ReactNode;
  reset?: () => void;
}

export const InvestmentDetail: FunctionComponent<InvestmentDetailProp> = ({
  stakedBalance,
  readableLPTokenBalance,
  makeClaim,
  view,
  pendingView,
  successView,
  errorView,
  reset,
}) => {
  const needEthModalRef = useRef<Modal>(null);
  const { farmEarned, selectedFarm } = useFarms();
  const { provider: exchangeProvider, selectedLiquidityPair } =
    useTrackedState();
  const selectedFiat = 'USD';
  const { account } = useEthers();

  const userAccount = account || EMPTY_ACCOUNT;

  const location = useLocation();
  const { newTx } = (location?.state as LocationState) || {
    newTx: false,
  };

  const ethBalance = useEtherBalance(userAccount);
  const showEthAlert = !ethBalance && exchangeProvider === 'bitcoincom_eth';

  const pair = useMemo(() => selectedLiquidityPair, [selectedLiquidityPair]);
  const provider = findProvider(exchangeProvider);
  const tickerA = useMemo(
    () => selectedLiquidityPair.token0?.symbol || '',
    [selectedLiquidityPair],
  );
  const tickerB = useMemo(
    () => selectedLiquidityPair.token1?.symbol || '',
    [selectedLiquidityPair],
  );
  const noLPTokens =
    !readableLPTokenBalance ||
    selectedFarm?.contractAddress === USDT_WZANO_FARM;

  const earnedAmount = parseFloat(
    formatValue(
      farmEarned[selectedFarm.contractAddress],
      selectedFarm.rewardToken.decimals || DEFAULT_DECIMALS,
    ),
  );

  const lpTokenExplorerLink = (u: string, token: string): string => {
    return `https://etherscan.io/token/${token}?a=${u}`;
  };

  const chainParam = useMemo(() => {
    return provider.chainParam || 'eth';
  }, [provider]);

  const pairParam = useMemo(() => {
    return `${pair?.token0?.symbol.toLowerCase()}-${pair?.token1?.symbol.toLowerCase()}`;
  }, [pair]);

  // Reset tx if navigating to tx view for first time
  useEffect(() => {
    if (typeof reset === 'function' && newTx) {
      reset();
      navigate('.', {
        state: { newTx: false },
        replace: true,
      });
    }
  }, []);

  const goBack = useCallback(() => {
    navigate(`/farms/${chainParam}/`);
  }, [chainParam]);

  const goDeposit = useCallback(() => {
    navigate(`/farms/${chainParam}/${pairParam}/deposit/`, {
      state: { newTx: true },
    });
  }, [chainParam, pairParam]);

  const goWithdraw = useCallback(() => {
    navigate(`/farms/${chainParam}/${pairParam}/withdraw/`, {
      state: { newTx: true },
    });
  }, [chainParam, pairParam]);

  const displayDefaultView =
    view !== 'pending' && view !== 'success' && view !== 'error';

  return (
    <>
      {view === 'pending' && pendingView}
      {view === 'success' && successView}
      {view === 'error' && errorView}
      {displayDefaultView && (
        <>
          <ScrollToTop />
          <DetailsWrapper>
            <DetailHeader>
              <StyledChevron onClick={goBack} />
              <CoinPair
                coinA={{
                  ticker: pair.token0?.symbol,
                  altSrc: findExternalIcon(
                    pair?.token0?.id,
                    pair?.token0?.symbol,
                    exchangeProvider,
                  ),
                  size: '44px',
                }}
                coinB={{
                  ticker: pair.token1?.symbol,
                  altSrc: findExternalIcon(
                    pair?.token1?.id,
                    pair?.token1?.symbol,
                    exchangeProvider,
                  ),
                  size: '44px',
                }}
              />
              <H2 style={{ padding: '16px 0 8px' }}>
                <FormattedMessage
                  id="farms.details.title"
                  values={{
                    PAIR: `${pair.token0?.symbol}/${pair.token1?.symbol}`,
                  }}
                />
              </H2>
            </DetailHeader>
            <InvestmentSummary>
              <PositionWrapper>
                <Card>
                  <Label style={{ color: colors.shade80 }}>
                    <FormattedMessage id="farms.details.deposits" />
                  </Label>
                  <H2>
                    <AmountFiat
                      amount={lpTokenValue(stakedBalance, pair)}
                      currency={selectedFiat}
                      locale="en-US"
                    />
                  </H2>
                  <Link
                    newTab
                    href={lpTokenExplorerLink(
                      account || '',
                      selectedLiquidityPair?.id,
                    )}
                    style={{ marginTop: 5.5 }}
                  >
                    <Body size="small" style={{ color: colors.shade90 }}>
                      {toFixed(stakedBalance, PRECISION)} VERSE-X ({tickerA}/
                      {tickerB}) <External size="10px" />
                    </Body>
                  </Link>
                  <Button
                    fullWidth
                    disabled={noLPTokens}
                    onClick={goDeposit}
                    style={{
                      marginTop: 16,
                      backgroundColor: colors.shade40,
                      fontSize: '14px',
                    }}
                  >
                    <FormattedMessage id="farms.global.deposit.depositButton" />
                  </Button>
                </Card>
              </PositionWrapper>
              <RewardsWrapper>
                <Card>
                  <Label style={{ color: colors.shade80 }}>
                    <FormattedMessage id="farms.global.rewards" />
                  </Label>
                  <CoinWrapper>
                    <Coin
                      alt={selectedFarm?.rewardToken?.abbr}
                      altSrc={findExternalIcon(
                        selectedFarm?.rewardToken?.token,
                        selectedFarm?.rewardToken?.abbr || 'VERSE',
                        exchangeProvider,
                      )}
                      ticker={selectedFarm?.rewardToken?.abbr || 'VERSE'}
                      size="24"
                    />
                    <Title>
                      {/* TODO: Replace this with contract.earned() value */}
                      <CountUp
                        end={earnedAmount || 0}
                        decimals={earnedAmount >= 1000 ? 0 : PRECISION}
                        duration={0.8}
                        preserveValue
                      />{' '}
                      {selectedFarm?.rewardToken?.abbr}
                    </Title>
                  </CoinWrapper>
                  <Button
                    fullWidth
                    design="secondary"
                    disabled={!earnedAmount}
                    onClick={() => {
                      if (showEthAlert) {
                        needEthModalRef?.current?.open();
                      } else {
                        makeClaim();
                      }
                    }}
                    style={{
                      backgroundColor: colors.shade40,
                      fontSize: '14px',
                    }}
                  >
                    <FormattedMessage id="farms.global.claim.claimButton" />
                  </Button>
                </Card>
              </RewardsWrapper>
              <ButtonsWrapper>
                <Card>
                  <DepositButton
                    fullWidth
                    small
                    disabled={noLPTokens}
                    onClick={goDeposit}
                    style={{ marginRight: 32 }}
                  >
                    <FormattedMessage id="farms.global.deposit.depositButton" />
                  </DepositButton>
                  <Button
                    fullWidth
                    small
                    design="secondary"
                    disabled={!earnedAmount}
                    onClick={() => {
                      if (showEthAlert) {
                        needEthModalRef?.current?.open();
                      } else {
                        makeClaim();
                      }
                    }}
                  >
                    <FormattedMessage id="farms.global.claim.claimButton" />
                  </Button>
                </Card>
              </ButtonsWrapper>
            </InvestmentSummary>
            <ClaimsSummary>
              <Card>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: colors.shade80,
                  }}
                >
                  <Label style={{ color: colors.shade80 }}>
                    <FormattedMessage id="farms.details.apy" />
                  </Label>
                  <Label style={{ color: colors.white }}>
                    {selectedFarm?.apy}%
                  </Label>
                </div>
              </Card>
            </ClaimsSummary>

            <WithdrawWrapper>
              <Button design="secondary" fullWidth small onClick={goWithdraw}>
                <FormattedMessage id="farms.details.withdrawClaim" />
              </Button>
            </WithdrawWrapper>
          </DetailsWrapper>
          <Modal
            ref={needEthModalRef}
            title={<FormattedMessage id="farms.global.needEth" />}
            maxWidth="500px"
          >
            <NeedEthModal />
          </Modal>
        </>
      )}
    </>
  );
};

export default InvestmentDetail;
